// Hooks
import { useSpring, animated, useTransition } from '@react-spring/web'
import { useRef, useState, useEffect } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'
import useBreakpoints from '@/hooks/breakpoints'
import { fetchBretzelRes } from '@/services/api/bretzel'

// Components
import Guide from '@/components/guides/GuideCard.js'
import Button from '@/components/ui/button'
import Image from 'next/legacy/image'

// Assets
import styles from './index.module.css'
import PlaneDown from '@/assets/img/guides/plane-down.png'

// Layout
import CenterLayout from '@/layouts/center'
import clsx from 'clsx'
import { SHARE_A_PLACE_LINK } from '@/components/constants'
import Head from '@/components/Head'

const Guides = ({ categories, guides }) => {
  const { isMobile } = useBreakpoints()
  const [displayedGuides, setDisplayedGuides] = useState(
    guides.slice(0, isMobile ? 1 : 2)
  )
  const ref = useRef(null)
  const [animationDone, setAnimationDone] = useState(false)
  const entry = useIntersectionObserver(ref, { threshold: [0] })

  const transitions = useTransition(displayedGuides, {
    from: { opacity: 0, transform: 'translateY(1000px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    trail: 200,
    config: { mass: 5, tension: 600, friction: 150 },
  })

  useEffect(() => {
    if (entry) {
      if (entry.isIntersecting) {
        setDisplayedGuides((before) => {
          const l = Math.min(before.length + (isMobile ? 1 : 2), guides.length)
          return guides.slice(0, l)
        })
      }
    }
  }, [entry?.boundingClientRect.y, guides, isMobile])

  return (
    <main className="container mb-50">
      <Head
        title={'Les guides de voyage Ulysse'}
        description={
          "Découvrez tous les bons plans conseillés par la communauté Ulysse. Rien de mieux que d'être conseillé pour bien voyager."
        }
      />
      <h1 className="z-2 text-center">
        <animated.span
          className="block text-dark-80 text-base"
          style={useSpring({
            to: { opacity: 1, y: 0 },
            from: { opacity: 0, y: -32 },
            delay: 400,
          })}
        >
          Explorez le monde avec
        </animated.span>
        <animated.span
          className="block font-bold text-60 md:text-[120px] leading-[70px] md:leading-[130px] text-transparent bg-clip-text guideGradient"
          style={useSpring({
            to: { opacity: 1, y: 0 },
            from: { opacity: 0, y: -32 },
            delay: 500,
          })}
        >
          les guides
        </animated.span>
        <animated.span
          className="block font-bold text-60 md:text-[120px] leading-[70px] md:leading-[130px]"
          style={useSpring({
            to: { opacity: 1, y: 0 },
            from: { opacity: 0, y: -32 },
            delay: 600,
          })}
        >
          Ulysse*
        </animated.span>
        <animated.span
          className="text-base block"
          style={useSpring({
            to: { opacity: 1, y: 0 },
            from: { opacity: 0, y: -1000 },
            delay: 600,
          })}
        >
          * par et pour vous
        </animated.span>
      </h1>
      <animated.div
        className={clsx(
          'relative top-0 left-0 right-0 mb-[-50px] h-[250px] md:h-[400px] lg:h-[500px] mt-10',
          { 'overflow-hidden': animationDone }
        )}
        style={useSpring({
          to: { opacity: 1, y: 0 },
          from: { opacity: 0, y: -1000 },
          onRest: () => setAnimationDone(true),
        })}
      >
        <Image src={PlaneDown} alt="Plane down" />
      </animated.div>
      <section className={styles.guides}>
        {categories?.length &&
          transitions(({ opacity, transform }, guide) => (
            <animated.div
              style={{ opacity, transform }}
              key={`guide-block-${guide.id}`}
            >
              <Guide
                key={`guide-${guide.id}`}
                guide={guide}
                categories={categories
                  .filter((cat) => cat !== undefined)
                  .filter(({ id }) => guide.categoryIds.includes(id))}
              />
            </animated.div>
          ))}
      </section>
      <section className="h-[70px]" ref={ref} />
      <section className={styles.share}>
        <div>
          <h2 className="mb-10">
            Vous avez une bonne adresse à nous partager ?
          </h2>
          <p>
            Ca tombe bien, toutes les bonnes adresses sont issues de la
            communauté Ulysse
          </p>
        </div>
        {SHARE_A_PLACE_LINK && (
          <Button
            unstyled
            className="guideGradient h-60 text-white font-bold rounded px-30 py-20 shadow-lg hover:shadow-high hover:translate-y-[-6px] active:shadow-md active:translate-y-0 motion-safe:transition-all motion-safe:duration-200"
            href={SHARE_A_PLACE_LINK}
          >
            ❤️ Partager une addresse
          </Button>
        )}
      </section>
    </main>
  )
}

export const getServerSideProps = async () => {
  try {
    const catPromise = fetchBretzelRes('place-categories')
    const guides = await fetchBretzelRes('guides')
    const categories = await catPromise

    return {
      props: {
        categories,
        guides,
      },
    }
  } catch (e) {
    console.error('Error while fetching the categories', e)
    return { redirect: { destination: '/500' } }
  }
}

Guides.layout = CenterLayout
export default Guides
